import { useEffect, useState } from "react";
import BrandQR from "../components/BrandQR";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError, ENABLE_MOCK_PAYMENTS } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Calendar,
  MapPin,
  Users,
  Ticket,
} from "lucide-react";

const LANG_KEY = "growithevent_lang";

const STRINGS = {
  en: {
    findMyTicket: "Find my ticket",
    loading: "Loading...",
    registeringFor: "You're registering for",
    registered: (r, c) => `${r} / ${c} registered`,
    ticketPrice: "Ticket price",
    closed: "Closed",
    closedTitle: "Registrations Closed (Event Full)",
    closedBody: "This event has reached its capacity.",
    alreadyRegistered: "Already registered? Find my ticket",
    fullName: "Full name",
    fullNamePlaceholder: "Jane Doe",
    sponsorName: "Sponsor name",
    sponsorPlaceholder: "Enter the name of your sponsor",
    email: "Email",
    optional: "(optional)",
    emailPlaceholder: "you@example.com",
    emailHint:
      "If provided, your QR ticket is emailed to you and you can use it to look up your ticket later.",
    phone: "Phone number",
    phonePlaceholder: "98765 43210",
    id: "ID",
    idPlaceholder: "Enter your Herbalife ID",
    firstTime: "First-time attendee?",
    firstTimeHint: "Is this your first time at this event?",
    ticketCount: "Number of tickets",
    ticketCountHint: "Maximum 50 tickets per booking.",
    processing: "Processing...",
    continueToPayment: (amount) => `Continue to payment · ₹${amount}`,
    demoNote:
      "Payments are currently running in demo mode. Connect a live gateway before public launch.",
    liveNote:
      "Payments are processed through your configured online payment gateway.",
  },
  hi: {
    findMyTicket: "मेरा टिकट खोजें",
    loading: "लोड हो रहा है...",
    registeringFor: "आप पंजीकरण कर रहे हैं",
    registered: (r, c) => `${r} / ${c} पंजीकृत`,
    ticketPrice: "टिकट मूल्य",
    closed: "बंद",
    closedTitle: "पंजीकरण बंद (सीटें भर गईं)",
    closedBody: "इस इवेंट की सभी सीटें भर चुकी हैं।",
    alreadyRegistered: "पहले से पंजीकृत हैं? मेरा टिकट खोजें",
    fullName: "पूरा नाम",
    fullNamePlaceholder: "आपका नाम",
    sponsorName: "स्पॉन्सर का नाम",
    sponsorPlaceholder: "अपने स्पॉन्सर का नाम लिखें",
    email: "ईमेल",
    optional: "(वैकल्पिक)",
    emailPlaceholder: "you@example.com",
    emailHint:
      "ईमेल देने पर आपका QR टिकट आपको ईमेल किया जाएगा और आप बाद में इससे अपना टिकट खोज सकेंगे।",
    phone: "फ़ोन नंबर",
    phonePlaceholder: "98765 43210",
    id: "ID",
    idPlaceholder: "अपनी Herbalife ID लिखें",
    firstTime: "पहली बार आ रहे हैं?",
    firstTimeHint: "क्या आप इस इवेंट में पहली बार शामिल हो रहे हैं?",
    ticketCount: "टिकटों की संख्या",
    ticketCountHint: "एक बुकिंग में अधिकतम 50 टिकट।",
    processing: "प्रोसेस हो रहा है...",
    continueToPayment: (amount) => `भुगतान के लिए आगे बढ़ें · ₹${amount}`,
    demoNote:
      "भुगतान फिलहाल डेमो मोड में चल रहे हैं। सार्वजनिक लॉन्च से पहले लाइव गेटवे कनेक्ट करें।",
    liveNote:
      "भुगतान आपके कॉन्फ़िगर किए गए ऑनलाइन पेमेंट गेटवे के माध्यम से प्रोसेस होते हैं।",
  },
};

function LanguageToggle({ lang, onChange }) {
  return (
    <div
      className="flex w-full rounded-md border border-zinc-200 overflow-hidden"
      data-testid="lang-toggle"
    >
      {[
        ["en", "English"],
        ["hi", "हिंदी"],
      ].map(([code, label]) => (
        <button
          key={code}
          type="button"
          onClick={() => onChange(code)}
          className={`flex-1 h-12 text-base font-medium transition-colors ${
            lang === code
              ? "bg-black text-white"
              : "bg-white text-zinc-500 hover:text-black"
          }`}
          data-testid={`lang-${code}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

export default function PublicRegistration() {
  const { eventId } = useParams();
  const [event, setEvent] = useState(null);
  const [lang, setLang] = useState(() =>
    localStorage.getItem(LANG_KEY) === "hi" ? "hi" : "en"
  );
  const [form, setForm] = useState({
    name: "",
    sponsor_name: "",
    email: "",
    phone: "",
    user_id_optional: "",
    first_time: false,
    ticket_count: 1,
  });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();

  const t = STRINGS[lang];

  const changeLang = (code) => {
    setLang(code);
    localStorage.setItem(LANG_KEY, code);
  };

  useEffect(() => {
    api
      .get(`/events/${eventId}`)
      .then((r) => setEvent(r.data))
      .catch((e) => setErr(formatApiError(e)));
  }, [eventId]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post(`/events/${eventId}/register`, {
        ...form,
        email: form.email.trim() ? form.email.trim() : undefined,
        phone: "+91" + form.phone.trim(),
        ticket_count: parseInt(form.ticket_count) || 1,
      });
      nav(`/pay/${data.registration.id}`);
    } catch (er) {
      toast.error(formatApiError(er));
    } finally {
      setLoading(false);
    }
  };

  if (err)
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full border border-zinc-200 rounded-md bg-white p-8 text-center">
          <p className="text-red-600 font-medium">{err}</p>
        </div>
      </div>
    );

  if (!event)
    return (
      <div className="min-h-screen flex items-center justify-center text-zinc-500">
        {t.loading}
      </div>
    );

  return (
    <div
      className="min-h-screen bg-[#FAFAFA] bg-no-repeat bg-cover"
      style={{
        backgroundImage:
          "linear-gradient(rgba(250,250,250,0.85),rgba(250,250,250,0.85)), url(https://images.unsplash.com/photo-1708199370329-4e9c67823075?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMGJhY2tncm91bmQlMjB3aGl0ZXxlbnwwfHx8fDE3NzY2OTIyNjB8MA&ixlib=rb-4.1.0&q=85)",
      }}
    >
      <div className="max-w-xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-2">
            <BrandQR />
            <span className="font-heading font-semibold tracking-tight">
              Growithevent
            </span>
          </div>
          <Link
            to="/my-tickets"
            className="text-xs text-zinc-500 hover:text-black inline-flex items-center gap-1 transition-colors"
            data-testid="my-tickets-link"
          >
            <Ticket className="w-3.5 h-3.5" />
            {t.findMyTicket}
          </Link>
        </div>

        <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
          {/* Event header */}
          <div className="p-8 border-b border-zinc-200">
            <p className="overline mb-3">{t.registeringFor}</p>
            <h1
              className="font-heading text-3xl sm:text-4xl font-semibold tracking-tighter mb-5"
              data-testid="public-event-name"
            >
              {event.name}
            </h1>
            <div className="space-y-1.5 text-sm text-zinc-600">
              <div className="flex items-center gap-2">
                <Calendar className="w-3.5 h-3.5" />
                {new Date(event.date).toLocaleString(
                  lang === "hi" ? "hi-IN" : [],
                  {
                    dateStyle: "medium",
                    timeStyle: "short",
                  }
                )}
              </div>
              {event.location && (
                <div className="flex items-center gap-2">
                  <MapPin className="w-3.5 h-3.5" />
                  {event.location}
                </div>
              )}
              <div className="flex items-center gap-2">
                <Users className="w-3.5 h-3.5" />
                {t.registered(event.registered, event.capacity)}
              </div>
            </div>
            <div className="mt-5 pt-5 border-t border-zinc-100 flex items-baseline justify-between">
              <span className="overline">{t.ticketPrice}</span>
              <span className="font-heading text-3xl font-semibold tracking-tight">
                ₹{event.price}
              </span>
            </div>
          </div>

          {/* Form or full state */}
          {event.is_full ? (
            <div className="p-12 text-center" data-testid="registrations-closed">
              <div className="mb-8">
                <LanguageToggle lang={lang} onChange={changeLang} />
              </div>
              <p className="overline text-red-600 mb-3">{t.closed}</p>
              <h2 className="font-heading text-2xl font-semibold tracking-tight mb-2">
                {t.closedTitle}
              </h2>
              <p className="text-zinc-500 mb-6">{t.closedBody}</p>
              <Link to="/my-tickets">
                <Button
                  variant="outline"
                  className="rounded-md border-zinc-200"
                  data-testid="full-state-my-tickets"
                >
                  {t.alreadyRegistered}
                </Button>
              </Link>
            </div>
          ) : (
            <form
              onSubmit={submit}
              className="p-8 space-y-5"
              data-testid="register-form"
            >
              <LanguageToggle lang={lang} onChange={changeLang} />

              <div>
                <Label className="text-sm font-medium mb-2 block">
                  {t.fullName}
                </Label>
                <Input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={t.fullNamePlaceholder}
                  className="h-11 rounded-md border-zinc-200 focus-visible:ring-black"
                  data-testid="reg-name-input"
                />
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">
                  {t.sponsorName}
                </Label>
                <Input
                  required
                  value={form.sponsor_name}
                  onChange={(e) =>
                    setForm({ ...form, sponsor_name: e.target.value })
                  }
                  placeholder={t.sponsorPlaceholder}
                  className="h-11 rounded-md border-zinc-200 focus-visible:ring-black"
                  data-testid="reg-sponsor-input"
                />
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">
                  {t.email}{" "}
                  <span className="text-zinc-400 font-normal">
                    {t.optional}
                  </span>
                </Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder={t.emailPlaceholder}
                  className="h-11 rounded-md border-zinc-200 focus-visible:ring-black"
                  data-testid="reg-email-input"
                />
                <p className="mt-1.5 text-xs text-zinc-500">{t.emailHint}</p>
              </div>

              {/* Phone */}
              <div>
                <Label className="text-sm font-medium mb-2 block">
                  {t.phone}
                </Label>
                <div className="flex h-11 rounded-md border border-zinc-200 overflow-hidden focus-within:ring-2 focus-within:ring-black">
                  <span className="flex items-center px-3 bg-zinc-100 text-zinc-600 text-sm font-medium border-r border-zinc-200 select-none">
                    +91
                  </span>
                  <Input
                    type="tel"
                    required
                    value={form.phone}
                    onChange={(e) =>
                      setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })
                    }
                    placeholder={t.phonePlaceholder}
                    className="h-full border-0 rounded-none focus-visible:ring-0 focus-visible:ring-offset-0"
                    data-testid="reg-phone-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">
                  {t.id}{" "}
                  <span className="text-zinc-400 font-normal">
                    {t.optional}
                  </span>
                </Label>
                <Input
                  value={form.user_id_optional}
                  onChange={(e) =>
                    setForm({ ...form, user_id_optional: e.target.value })
                  }
                  placeholder={t.idPlaceholder}
                  className="h-11 rounded-md border-zinc-200 focus-visible:ring-black"
                  data-testid="reg-id-input"
                />
              </div>

              <div className="flex items-center justify-between p-4 border border-zinc-200 rounded-md">
                <div>
                  <p className="font-medium text-sm">{t.firstTime}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {t.firstTimeHint}
                  </p>
                </div>
                <Switch
                  checked={form.first_time}
                  onCheckedChange={(v) => setForm({ ...form, first_time: v })}
                  data-testid="reg-firsttime-switch"
                />
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">
                  {t.ticketCount}
                </Label>
                <Input
                  type="number"
                  min={1}
                  max={50}
                  required
                  value={form.ticket_count}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setForm({
                      ...form,
                      ticket_count:
                        raw === ""
                          ? ""
                          : Math.max(0, Math.min(50, parseInt(raw) || 0)),
                    });
                  }}
                  className="h-11 rounded-md border-zinc-200 focus-visible:ring-black w-32"
                  data-testid="reg-ticket-count-input"
                />
                <p className="mt-1.5 text-xs text-zinc-500">
                  {t.ticketCountHint}
                </p>
              </div>

              <Button
                type="submit"
                disabled={loading || !(parseInt(form.ticket_count) >= 1)}
                className="w-full h-11 bg-black text-white hover:bg-zinc-800 rounded-md mt-2"
                data-testid="reg-submit-button"
              >
                {loading
                  ? t.processing
                  : t.continueToPayment(
                      event.price * (parseInt(form.ticket_count) || 0)
                    )}
              </Button>
            </form>
          )}
        </div>

        <p className="text-xs text-zinc-400 text-center mt-6">
          {ENABLE_MOCK_PAYMENTS ? t.demoNote : t.liveNote}
        </p>
      </div>
    </div>
  );
}
